<template>
  <div id="SectionImpact">
    <UIContainer>
      <SectionTitle class="SectionImpact__title">
        <template #title>
          Support at any stage of your sustainability journey
        </template>
      </SectionTitle>

      <div class="SectionImpact__cards">
        <div
          class="SectionImpact__card"
          v-for="(card, index) in cards"
          :class="`SectionImpact__card--${index + 1}`"
          :key="card.title"
        >
          <div>
            <div class="SectionImpact__card-number">Step {{ index + 1 }}</div>
            <h3 class="SectionImpact__card-title">{{ card.title }}</h3>
            <p class="SectionImpact__card-description">
              {{ card.description }}
            </p>
          </div>

          <!-- <div v-if="index === 1" class="SectionImpact__card-icon-container">
            <UIIcon name="targetCheck" class="SectionImpact__card-icon" />
          </div> -->
          <img
            :src="card.image"
            :alt="card.title"
            class="SectionImpact__card-image"
            loading="lazy"
          />
        </div>
      </div>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import impactStep1 from '../assets/impactStep1.svg'
import impactStep2 from '../assets/impactStep2.svg'
import impactStep3 from '../assets/impactStep3.svg'

const cards = [
  {
    image: impactStep1,
    title: 'Collect and measure',
    description:
      'Gather business data through automated workflows and APIs to reliably measure your complete carbon footprint.',
  },
  {
    image: impactStep2,
    title: 'Reduce & make impact',
    description:
      'Set data-driven decarbonization targets and create a clear reduction trajectory for your company.',
  },
  {
    image: impactStep3,
    title: 'Report and share',
    description:
      'Effortlessly generate auditable GHG assessments and downloadable reports to share your commitment both internally and externally.',
  },
]
</script>

<style lang="scss" scoped>
#SectionImpact {
  .SectionImpact__title {
    max-width: 48rem;
    text-align: center;
    margin: 0 auto 1rem;
  }

  .SectionImpact__cards {
    margin-top: 4rem;
    display: flex;
    gap: 1.5rem;
    justify-content: center;
    flex-wrap: wrap;

    @include sm {
      gap: 2rem;
    }

    @include md {
      gap: 2.75rem;
    }
  }

  /* Shadows/shadow-xl */
  .SectionImpact__card {
    /* Ensure cards wrap when they'd be narrower than 18rem */
    flex: 1 1 18rem; /* grow, shrink, preferred width */
    min-width: 18rem;
    max-width: 24rem;
    position: relative;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 420px;
    border-radius: 1rem;
    box-shadow:
      0 20px 24px -4px
        var(--Colors-Effects-Shadows-shadow-xl_01, rgba(0, 16, 53, 0.08)),
      0 8px 8px -4px
        var(--Colors-Effects-Shadows-shadow-xl_02, rgba(0, 16, 53, 0.03)),
      0 3px 3px -1.5px
        var(--Colors-Effects-Shadows-shadow-xl_03, rgba(0, 16, 53, 0.04));
    overflow: hidden;

    &--1 {
      background-color: $color-white;

      .SectionImpact__card-image {
        right: 14px;
        bottom: 0;
      }
    }
    &--2 {
      background-color: $color-ui-gray-cool-50;

      .SectionImpact__card-image {
        left: 0;
      }
    }
    &--3 {
      background-color: $color-ui-gray-cool-100;

      .SectionImpact__card-image {
        left: 0;
      }
    }
  }

  .SectionImpact__card-number {
    @include font-size-16px();
    margin-bottom: 1.25rem;
    border-radius: 0.75rem;
    border: 1px solid $color-ui-gray-cool-700;
    color: $color-ui-gray-cool-800;
    font-weight: 600;
    padding: 0.25rem 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;

    @include md {
      @include font-size-20px();
    }
  }

  .SectionImpact__card-title {
    @include font-size-18px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    margin-bottom: 0.25rem;

    @include sm {
      @include font-size-20px();
    }

    @include md {
      @include font-size-24px();
    }
  }

  .SectionImpact__card-description {
    @include font-size-14px();
    color: $color-ui-gray-cool-900;
    margin-bottom: 1rem;
    line-height: 1.5;

    @include md {
      @include font-size-16px();
    }
  }

  .SectionImpact__card-button {
    @include font-size-14px();
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: $color-ui-gray-cool-600;
    font-weight: 600;
    transition: color 0.2s ease;

    &:hover {
      color: $color-ui-gray-cool-900;
    }

    @include sm {
      @include font-size-14px();
    }

    @include md {
      @include font-size-16px();
    }
  }

  .SectionImpact__card-image {
    bottom: 1.5rem;
    width: 100%;
  }

  .SectionImpact__card-icon-container {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: $color-ui-brand-100;
    position: absolute;
    right: 1.5rem;
    bottom: 8.5rem;
  }

  .SectionImpact__card-icon {
    --size: 2rem;
  }
}
</style>
